// web/src/components/browser/ListView.tsx
import { useNavigate } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { cn } from "../../lib/utils";
import type { DirEntry } from "../../worker/api";
import { Thumbnail } from "../primitives/Thumbnail";
import { useNavigation } from "../../stores/navigation";
import { useLongPress } from "../../hooks/useLongPress";
import { isMediaEntry } from "./entryKind";

function humanSize(n: number | undefined): string {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let x = n;
  while (x >= 1024 && i < u.length - 1) {
    x /= 1024;
    i++;
  }
  return `${x.toFixed(x >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

export function ListView({
  vaultId,
  folderPath,
  entries,
  onKebab,
  onTapFile,
  onTapPhoto,
}: {
  vaultId: string;
  folderPath: string;
  entries: DirEntry[];
  onKebab?: (name: string) => void;
  onTapFile: (name: string) => void;
  onTapPhoto: (name: string) => void;
}) {
  const navigate = useNavigate();
  const sort = useNavigation((s) => s.sort);
  const setSort = useNavigation((s) => s.setSort);

  const sorted = [...entries].sort((a, b) => {
    // folders first always
    if (a.kind === "dir" && b.kind !== "dir") return -1;
    if (b.kind === "dir" && a.kind !== "dir") return 1;
    let cmp = 0;
    if (sort.col === "size") cmp = (a.size ?? 0) - (b.size ?? 0);
    else if (sort.col === "modified")
      cmp = (a.modified ?? 0) - (b.modified ?? 0);
    else if (sort.col === "type") cmp = (a.mime ?? "").localeCompare(b.mime ?? "");
    else cmp = a.name.localeCompare(b.name);
    return sort.dir === "asc" ? cmp : -cmp;
  });

  function headerBtn(label: string, col: typeof sort.col) {
    return (
      <button
        onClick={() =>
          setSort({
            col,
            dir: sort.col === col && sort.dir === "asc" ? "desc" : "asc",
          })
        }
        className="text-[10px] uppercase tracking-[0.12em] text-text-dim hover:text-text"
      >
        {label}
        {sort.col === col ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
      </button>
    );
  }

  const openEntry = (e: DirEntry) => {
    if (e.kind === "dir") {
      navigate(
        `/v/${vaultId}/~/${encodeURIComponent(
          folderPath ? `${folderPath}/${e.name}` : e.name,
        )}`,
      );
    }
  };

  return (
    <div className="px-3 py-3 md:px-5">
      <div className="hidden grid-cols-[26px_1fr_110px_100px_90px] gap-3 border-b border-border px-1 py-2 md:grid">
        <span />
        {headerBtn("Name", "name")}
        {headerBtn("Size", "size")}
        {headerBtn("Modified", "modified")}
        {headerBtn("Type", "type")}
      </div>
      {sorted
        .filter((e) => !e.name.startsWith("."))
        .map((e) => (
          <Row
            key={e.name}
            entry={e}
            folderPath={folderPath}
            onOpen={() => openEntry(e)}
            onKebab={onKebab}
            onTapFile={onTapFile}
            onTapPhoto={onTapPhoto}
          />
        ))}
    </div>
  );
}

function Row({
  entry: e,
  folderPath,
  onOpen,
  onKebab,
  onTapFile,
  onTapPhoto,
}: {
  entry: DirEntry;
  folderPath: string;
  onOpen: () => void;
  onKebab?: (name: string) => void;
  onTapFile: (name: string) => void;
  onTapPhoto: (name: string) => void;
}) {
  const selection = useNavigation((s) => s.selection);
  const active = useNavigation((s) => s.selectionActive);
  const toggle = useNavigation((s) => s.toggleSelection);
  const activate = useNavigation((s) => s.activateSelection);
  const { didLongPress, ...pressHandlers } = useLongPress(() =>
    activate(e.name),
  );
  const isSelected = selection.has(e.name);

  const onClick = (ev: React.MouseEvent) => {
    if (didLongPress()) return;
    if (active) {
      toggle(e.name);
      return;
    }
    if (ev.shiftKey || ev.metaKey || ev.ctrlKey) {
      toggle(e.name);
      return;
    }
    if (e.kind === "dir") {
      onOpen();
    } else if (isMediaEntry(e)) {
      onTapPhoto(e.name);
    } else {
      onTapFile(e.name);
    }
  };

  return (
    <div
      {...pressHandlers}
      onClick={onClick}
      onDoubleClick={onOpen}
      className={cn(
        "flex items-center gap-3 border-b border-border-soft px-1 py-3 text-text-soft hover:bg-white/2",
        "md:grid md:grid-cols-[26px_1fr_110px_100px_90px] md:py-1.5 md:text-xs",
        isSelected && "bg-accent-bg text-text",
      )}
    >
      <div className="h-10 w-10 overflow-hidden rounded-md bg-white/5 md:h-4 md:w-4 md:rounded-sm">
        {e.kind === "dir" ? (
          <span className="block h-full w-full bg-accent-soft/70" />
        ) : e.mime?.startsWith("image/") ? (
          <Thumbnail
            folderPath={`/${folderPath}`}
            fileName={e.name}
            mime={e.mime}
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-[10px] font-bold text-accent-soft md:text-[7px]">
            {e.name.split(".").pop()?.slice(0, 3).toUpperCase() ?? "F"}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 md:contents">
        <div className="truncate text-sm text-text md:text-xs">{e.name}</div>
        <div className="font-mono text-[11px] text-text-dim md:hidden">
          {e.kind === "dir"
            ? "Folder"
            : `${humanSize(e.size)} · ${
                e.modified
                  ? new Date(e.modified * 1000).toLocaleDateString()
                  : "—"
              }`}
        </div>
      </div>
      <span className="hidden font-mono text-[11px] md:inline">
        {humanSize(e.size)}
      </span>
      <span className="hidden font-mono text-[11px] md:inline">
        {e.modified ? new Date(e.modified * 1000).toLocaleDateString() : "—"}
      </span>
      <span className="hidden font-mono text-[11px] md:inline">
        {e.kind === "dir" ? "folder" : (e.mime ?? "file")}
      </span>
      {onKebab && (
        <button
          type="button"
          aria-label="More options"
          onClick={(ev) => {
            ev.stopPropagation();
            onKebab(e.name);
          }}
          className="-mr-1 grid h-10 w-10 shrink-0 place-items-center rounded-md text-text-dim hover:bg-accent-bg hover:text-text md:hidden"
        >
          <MoreVertical className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
